/* eslint-disable max-len */
import React, { useState, useRef, useEffect } from "react";
import { string, shape, object } from "prop-types";
import get from "lodash/get";
import { useSelector, useDispatch } from "react-redux";
import throttle from "lodash/throttle";

import Separator from "../../atoms/separator";
import SectionBreadcrum from "../../atoms/section-breadcrum";

import Pagination from "../../molecules/pagination";
import StoryCard15 from "../../molecules/story-card15";
import TitleWithBorder from "../../molecules/title-with-border";

import { getCurrentPageFromUrl, updateListingPageUrl } from "../../helper/utils";
import { getCollection } from "../../helper/api";
import { setLoading } from "../../../components/store/actions";
import { CollectionOfCollectionPage } from "../collection-of-collection-page";

import { AdContainer } from "../../ads";
import TaboolaAd from "../../ads/taboola-ad";
import TaboolaScript from "../../ads/taboola-ad/load-taboola-ad";
import NewTopBanner from "../../atoms/new-top-banner";

import { SubSectionPage } from "../sub-section-page";

import "./section-page.m.css";

const SectionPage = ({ data }) => {
  const [isMobile, setMobile] = useState(null);
  const [renderTaboolaAd, setRenderTaboolaAd] = useState(false);
  const [sectionStories, setSectionStories] = useState([]);
  const [pageNum, setPageNum] = useState(data?.pageNum || 1);
  const [isInitialized, setIsInitialized] = useState(false);

  const dispatch = useDispatch();
  const ref = useRef();
  const qtState = useSelector((state) => get(state, ["qt"], {}));

  if (!data?.collection?.items?.length) return null;

  const { collection, section } = data;
  const parentId = get(qtState, ["data", "section", "parent-id"], null);
  const stories = collection?.items?.filter((item) => item?.type === "story").map((i) => i.story);
  const pageTitleRaw = collection?.metadata?.["display-name"] || collection?.name || section?.name;
  const pageTitle = pageTitleRaw?.replace(/\([^)]*\)/g, "") || "";
  const totalCount = get(data, ["collection", "total-count"], 0);
  const cardsPerPage = 24;

  // unified helpers moved to utils

  const loadStories = async (page, skipUrlUpdate = false) => {
    const offset = (page - 1) * cardsPerPage;
    const slug = data.collection?.slug;

    dispatch(setLoading(true));
    if (!skipUrlUpdate) {
      ref.current?.scrollIntoView({ behavior: "smooth" });
    }

    try {
      const loadedStories = await getCollection(slug, offset, cardsPerPage);
      setSectionStories(loadedStories);
      setPageNum(page);

      if (!skipUrlUpdate) updateListingPageUrl(page);
    } catch (error) {
      console.error("Error loading stories:", error);
    } finally {
      dispatch(setLoading(false));
      setIsInitialized(true);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== "undefined") {
        setMobile(window.innerWidth < 767);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const getScroll = () => {
      if (typeof window !== "undefined" && window.scrollY > 500) {
        setRenderTaboolaAd(true);
      }
    };
    const throttledScroll = throttle(getScroll, 500);
    window.addEventListener("scroll", throttledScroll);
    return () => window.removeEventListener("scroll", throttledScroll);
  }, []);

  useEffect(() => {
    const urlPage = getCurrentPageFromUrl();
    if (urlPage > 1) {
      loadStories(urlPage, true);
    } else {
      setSectionStories(stories || []);
      setPageNum(1);
      setIsInitialized(true);
    }
  }, [data?.collection?.slug]);

  if (!isInitialized) return null;
  if (!stories?.length && pageNum === 1) return <CollectionOfCollectionPage data={data} />;
  if (parentId) return <SubSectionPage data={data} isSection={true} title={pageTitle} />;

  const currentPath = get(qtState, ["currentPath"], "").split("?")[0];

  const desktopStories = (items) => (
    <div styleName="wrapper" className="container">
      <TaboolaScript type="category" />
      <Separator />
      {!isMobile && (
        <div className="hide-mobile ad ad-bgc">
          <AdContainer
            AdClassName="standard-970x250"
            id="div-gpt-ad-1680156194475-0"
            desktop="DH_Desktop_HP_Billboard_Top"
          />
        </div>
      )}
      <SectionBreadcrum initialPath={currentPath} type="section-page" />
      <TitleWithBorder name={pageTitle} />

      <div styleName="stories">
        {items.slice(0, 8).map((story, index) => (
          <StoryCard15 story={story} key={index} />
        ))}
      </div>

      {items.slice(8, 16).length > 0 && (
        <>
          <Separator />
          {!isMobile && (
            <div className="hide-mobile ad">
              <AdContainer id="div-gpt-ad-1680156254107-0" desktop="DH_Desktop_HP_Billboard_Mid" />
            </div>
          )}
          <Separator />
        </>
      )}

      <div styleName="stories">
        {items.slice(8, 16).map((story, index) => (
          <StoryCard15 story={story} key={index} />
        ))}
      </div>

      {items.slice(16, 24).length > 0 && (
        <>
          <Separator />
          {!isMobile && (
            <div className="hide-mobile ad">
              <AdContainer id="div-gpt-ad-1680155832150-0" desktop="DH_Desktop_HP_Billboard_Bottom" />
            </div>
          )}
        </>
      )}

      <Separator />
      <div styleName="stories">
        {items.slice(16, 24).map((story, index) => (
          <StoryCard15 story={story} key={index} />
        ))}
      </div>

      <Separator />
      {totalCount && (
        <Pagination
          loadMoreStories={(page) => loadStories(page)}
          totalItems={totalCount}
          perPage={cardsPerPage}
          initialPage={pageNum}
        />
      )}

      {renderTaboolaAd && (
        <TaboolaAd
          container="taboola-below-category-thumbnails"
          placement="Below Category Thumbnails"
          mode="thumbnails-a"
        />
      )}
    </div>
  );

  return (
    <div ref={ref}>
      {!isMobile && <NewTopBanner sectionPage={true}/>}
      {!isMobile && isMobile !== null && <div className="hide-mobile">{desktopStories(sectionStories)}</div>}
      {isMobile && (
        <div className="hide-desktop">
          <SubSectionPage data={data} isSection={true} title={pageTitle} />
        </div>
      )}
    </div>
  );
};

SectionPage.propTypes = {
  pageType: string,
  data: shape({
    collection: object,
    section: object
  })
};

export { SectionPage };
